/**
 * {@link AdminManagement} — add admins by email/userId, remove admins (with
 * last-admin guard), and list current admins.
 *
 * Requirements: 2.2, 2.3
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import { ConfirmDialog } from './ConfirmDialog.js';
import type { CSSProperties } from 'react';

export interface AdminItem {
  userId: string;
  email?: string;
  name?: string;
}

export interface AdminManagementProps {
  eventId: string;
  apiClient: ApiClient;
}

const sectionStyle: CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '1rem',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const listItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 0',
  borderBottom: '1px solid var(--color-border, #E5E7EB)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  borderRadius: 'var(--radius-button, 8px)',
  border: '1px solid var(--color-border, #E5E7EB)',
};

export function AdminManagement({
  eventId,
  apiClient,
}: AdminManagementProps): JSX.Element {
  const [admins, setAdmins] = useState<AdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addInput, setAddInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AdminItem | null>(null);
  const [removing, setRemoving] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.request<{ admins: AdminItem[] }>(
        'GET',
        `/events/${encodeURIComponent(eventId)}/admins`,
      );
      setAdmins(data.admins);
      setError(null);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load admins.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, eventId]);

  useEffect(() => {
    void fetchAdmins();
  }, [fetchAdmins]);

  async function handleAdd(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const value = addInput.trim();
    if (!value) return;

    setAdding(true);
    setError(null);
    try {
      // The backend accepts either email or userId in the body
      await apiClient.request<unknown>(
        'POST',
        `/events/${encodeURIComponent(eventId)}/admins`,
        { body: { email: value } },
      );
      setAddInput('');
      await fetchAdmins();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to add admin.');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(): Promise<void> {
    if (!removeTarget) return;
    setRemoving(true);
    setError(null);
    try {
      await apiClient.request<void>(
        'DELETE',
        `/events/${encodeURIComponent(eventId)}/admins/${encodeURIComponent(removeTarget.userId)}`,
      );
      setRemoveTarget(null);
      await fetchAdmins();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to remove admin.');
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  }

  const isLastAdmin = admins.length <= 1;

  return (
    <section style={sectionStyle} aria-labelledby="admin-mgmt-heading">
      <h3 id="admin-mgmt-heading" style={headingStyle}>
        Admins
      </h3>

      {error && (
        <ErrorBanner title="Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {/* Add admin form */}
      <form
        onSubmit={(e) => { void handleAdd(e); }}
        style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}
        noValidate
      >
        <label htmlFor="add-admin-input" style={{ display: 'none' }}>
          Admin email or user ID
        </label>
        <input
          id="add-admin-input"
          type="text"
          placeholder="Add admin by email"
          value={addInput}
          onChange={(e) => setAddInput(e.target.value)}
          style={inputStyle}
          aria-label="Admin email or user ID"
        />
        <Button type="submit" disabled={adding || !addInput.trim()}>
          {adding ? <Spinner size="1rem" label="Adding…" /> : 'Add Admin'}
        </Button>
      </form>

      {/* Admin list */}
      {loading ? (
        <Spinner label="Loading admins…" />
      ) : admins.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No admins found.</p>
      ) : (
        <ul style={listStyle} aria-label="Admin list">
          {admins.map((admin) => (
            <li key={admin.userId} style={listItemStyle}>
              <span style={{ fontWeight: 500 }}>
                {admin.name || admin.email || admin.userId}
              </span>
              <Button
                variant="secondary"
                onClick={() => setRemoveTarget(admin)}
                disabled={isLastAdmin}
                aria-label={`Remove admin ${admin.name || admin.email || admin.userId}`}
                title={isLastAdmin ? 'Cannot remove the last admin' : undefined}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {isLastAdmin && !loading && admins.length > 0 && (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)', fontSize: 'var(--font-size-body-sm, 0.875rem)', marginTop: '0.5rem' }}>
          The last admin cannot be removed from an event.
        </p>
      )}

      {/* Confirm remove dialog */}
      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove Admin"
        message={`Are you sure you want to remove ${removeTarget?.name || removeTarget?.email || 'this admin'} as an admin? They will lose all event management permissions.`}
        confirmLabel={removing ? 'Removing…' : 'Remove'}
        onConfirm={() => { void handleRemove(); }}
        onCancel={() => setRemoveTarget(null)}
      />
    </section>
  );
}
